import { clearSessionStorage } from "../../../utils/clearSessionStorage";
import { FirebaseAuth } from "../../../utils/firebase";
import { APP_V2_APP_EVENTS, logError } from "../../lib/analytics";
import { cognitoSignOut, getCognitoIdToken } from "./cognitoAuth";

/**
 * sessionStorage key used to persist the revocation toast message across the
 * full-page redirect to /enter.  The login page reads and clears this key
 * on initialisation so the user sees the message after the reload.
 */
export const COGNITO_SESSION_REVOKED_TOAST_KEY = "cognito_session_revoked_toast";

const SESSION_REVOKED_MESSAGE =
  "Your session has been revoked. This may happen when your account credentials are updated. Please log in again.";

let isLoggingOut = false;

/**
 * Checks whether an error thrown by `fetchAuthSession` / `getCognitoIdToken`
 * indicates that the Cognito refresh token has been revoked (e.g. after a
 * global sign-out triggered by an email or phone change).
 */
export function isCognitoSessionRevokedError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const name = "name" in error ? (error as { name: string }).name : "";
  const message = error.message ?? "";

  // Amplify v6 catches NotAuthorizedException during token refresh
  // and may also surface TokenRevokedException in some scenarios.
  if (name === "NotAuthorizedException" || name === "TokenRevokedException") {
    return true;
  }

  // Catch messages that explicitly mention revocation
  const revokedPatterns = [
    "Refresh Token has been revoked",
    "Access Token has been revoked",
    "refresh token has expired",
  ];
  return revokedPatterns.some((pattern) => message.includes(pattern));
}

/**
 * Checks whether an axios error represents a 401 Unauthorized or 403 Forbidden response.
 */
export function isAxiosAuthError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const maybeAxiosError = error as { response?: { status?: number } };
  const status = maybeAxiosError.response?.status;
  return status === 401 || status === 403;
}

/**
 * Verifies whether the current Cognito session is still valid by attempting
 * a forced token refresh. Returns `true` if the session is revoked/expired
 * (i.e. the refresh fails), `false` if the session is still valid.
 *
 * This avoids false-positive logouts when a 401/403 is returned for
 * permission-related reasons rather than session revocation.
 */
export async function isCognitoSessionRevoked(): Promise<boolean> {
  try {
    const token = await getCognitoIdToken(true);
    return !token;
  } catch (error: unknown) {
    return isCognitoSessionRevokedError(error);
  }
}

/**
 * Forces a full logout when a Cognito session is detected as revoked.
 *
 * This signs out from both Cognito and Firebase, clears session storage,
 * shows an error toast, and redirects to the login page.
 *
 * A guard prevents multiple concurrent logout attempts (e.g. when several
 * in-flight API calls all return 401/403 simultaneously).
 */
export async function handleCognitoSessionRevoked(context?: {
  error?: unknown;
  source?: string;
}): Promise<void> {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  try {
    logError(APP_V2_APP_EVENTS.COGNITO_SESSION_REVOKED_LOGOUT, {
      error: context?.error instanceof Error ? context.error : new Error("Cognito session revoked"),
      metadata: {
        source: context?.source ?? "unknown",
      },
    });

    // Persist the toast message in sessionStorage BEFORE any async work.
    // FirebaseAuth.signOut() triggers onIdTokenChanged(null) which may
    // redirect to /enter before subsequent lines execute.  By writing
    // the key first we guarantee the login page can display the toast
    // regardless of which code path wins the redirect race.
    sessionStorage.setItem(COGNITO_SESSION_REVOKED_TOAST_KEY, SESSION_REVOKED_MESSAGE);

    // Clear all session-related localStorage keys (does NOT touch sessionStorage)
    clearSessionStorage();

    // Sign out from both Cognito and Firebase in parallel.
    // Both are best-effort — we continue with the redirect even if either fails.
    await Promise.all([cognitoSignOut().catch(() => {}), FirebaseAuth.signOut().catch(() => {})]);

    // Redirect to login page
    window.location.href = "/enter";
  } finally {
    // Reset the guard after a short delay to allow the redirect to complete.
    // If the redirect fails for some reason, this ensures the guard is reset
    // so a subsequent attempt can retry.
    setTimeout(() => {
      isLoggingOut = false;
    }, 5000);
  }
}
